import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Text,
  Dropdown,
  DropdownButton,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  ActionListItemIcon,
  CheckIcon,
  Spinner,
  ChevronDownIcon,
} from '@razorpay/blade/components';
import { toast } from 'react-toastify';
import { useAppSelector } from '../store';
import {
  selectMerchants,
  selectSelectedMerchant,
  selectIsMultiMerchantUser,
  setMerchants,
  setSelectedMerchant,
} from '../store/slices/merchantSlice';
import { getUserMerchants } from '../api/merchantApi';
import { useAuth } from '../hooks/useAuth';
import { getErrorMessage, getErrorStatus } from '../utils/errorMessage';
import { filterActiveMerchants, type Merchant } from '../types/merchant';

/** Delay (ms) before reload after merchant switch so the toast is visible. */
const MERCHANT_SWITCH_RELOAD_DELAY_MS = 500;

export function MerchantSelector() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();
  const merchants = useAppSelector(selectMerchants);
  const selectedMerchant = useAppSelector(selectSelectedMerchant);
  const isMultiMerchant = useAppSelector(selectIsMultiMerchantUser);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isLoadingMerchants, setIsLoadingMerchants] = useState(false);
  const fetchAttempted = useRef(false);
  const isFetchingRef = useRef(false);

  // When authenticated but merchants empty (e.g. Office Head, or existing session), fetch merchants
  useEffect(() => {
    if (
      !isAuthenticated ||
      merchants.length > 0 ||
      fetchAttempted.current ||
      isFetchingRef.current
    ) {
      return;
    }
    fetchAttempted.current = true;
    isFetchingRef.current = true;
    setIsLoadingMerchants(true);

    getUserMerchants()
      .then((allMerchants) => {
        // Filter to active merchants only
        const activeMerchants = filterActiveMerchants(allMerchants);
        if (activeMerchants?.length) {
          dispatch(setMerchants(activeMerchants));
          dispatch(setSelectedMerchant(activeMerchants[0]));
        } else {
          toast.error(
            'No active merchants found. Please contact your administrator.',
          );
        }
      })
      .catch((error) => {
        const status = getErrorStatus(error);
        if (status === 403) {
          toast.error('You do not have permission to access merchant data.');
        } else if (status === 401) {
          toast.error('Your session has expired. Please log in again.');
        } else {
          toast.error(
            getErrorMessage(
              error,
              'Failed to load merchants after multiple attempts. Please try again.',
            ),
          );
        }
        console.error('Failed to fetch merchants:', error);
      })
      .finally(() => {
        isFetchingRef.current = false;
        setIsLoadingMerchants(false);
      });
  }, [isAuthenticated, merchants.length, dispatch]);

  if (!isAuthenticated) {
    return null;
  }

  if (isLoadingMerchants) {
    return (
      <Box display="flex" alignItems="center" minWidth="spacing.10">
        <Spinner
          size="medium"
          color="primary"
          accessibilityLabel="Loading merchants"
        />
      </Box>
    );
  }

  if (merchants.length === 0) {
    return null;
  }

  const handleSelect = (merchant: Merchant) => {
    if (merchant.merchant_id === selectedMerchant?.merchant_id) return;

    setIsSwitching(true);

    // Show informative toast before switch
    toast.info(
      `Switching to ${merchant.merchant_name} (${merchant.merchant_country})...`,
      { autoClose: 3000 },
    );

    dispatch(setSelectedMerchant(merchant));

    // Small delay to show toast before reload
    setTimeout(() => {
      window.location.reload();
    }, MERCHANT_SWITCH_RELOAD_DELAY_MS);
  };

  if (!isMultiMerchant && selectedMerchant) {
    return (
      <Box display="flex" alignItems="center">
        <Text variant="body" size="medium" weight="medium">
          {selectedMerchant.merchant_name}
        </Text>
      </Box>
    );
  }

  return (
    <Dropdown>
      <DropdownButton
        variant="tertiary"
        size="medium"
        isDisabled={isSwitching}
        iconPosition="right"
        icon={ChevronDownIcon}
      >
        {selectedMerchant?.merchant_name ?? 'Select merchant'}
      </DropdownButton>
      <DropdownOverlay width="240px">
        <ActionList>
          {merchants.map((merchant) => (
            <ActionListItem
              key={merchant.merchant_id}
              title={merchant.merchant_name}
              value={merchant.merchant_id}
              isSelected={
                selectedMerchant?.merchant_id === merchant.merchant_id
              }
              leading={
                selectedMerchant?.merchant_id === merchant.merchant_id ? (
                  <ActionListItemIcon icon={CheckIcon} />
                ) : undefined
              }
              onClick={() => handleSelect(merchant)}
            />
          ))}
        </ActionList>
      </DropdownOverlay>
    </Dropdown>
  );
}
